import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Button,
  Stack,
  Divider,
  Link,
} from '@mui/material';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import ContactForm from '../components/ContactForm';
import SectionHeading from '../components/SectionHeading';
import LocationMap from '../components/LocationMap';
import { gradients } from '../theme/theme';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { trackEvent } from '../utils/analytics';

// Coordinates of Tepebaşı Mah. 644 Sok., Kızıltepe (update with exact pin if needed)
const MAP_LAT = 37.1976;
const MAP_LNG = 40.5896;
const ADDRESS_QUERY = encodeURIComponent('Tepebaşı Mahallesi 644 Sokak No:5B Kızıltepe Mardin');
// Directions / full map open in Google Maps where address geocoding works well
const MAP_LINK = `https://www.google.com/maps/search/?api=1&query=${ADDRESS_QUERY}`;

const ContactPage = () => {
  const { t } = useTranslation();

  const contactInfo = [
    {
      icon: LocationOnIcon,
      label: t('contact.info.address'),
      href: MAP_LINK,
      external: true,
      color: '#1565C0',
      event: 'map_link_click',
      eventSource: 'contact_info_address',
    },
    {
      icon: EmailIcon,
      label: t('contact.info.email'),
      href: 'mailto:miran.iletisimlojistik@hotmail.com',
      color: '#00897B',
      event: 'email_click',
      eventSource: 'contact_info_email',
    },
    {
      icon: PhoneIcon,
      label: t('contact.info.phone'),
      href: 'tel:+905325525984',
      color: '#5C6BC0',
      event: 'phone_click',
      eventSource: 'contact_info_phone',
    },
    {
      icon: AccessTimeIcon,
      label: t('contact.info.workHours'),
      color: '#FF7043',
    },
  ];

  return (
    <Box
      sx={{
        pt: { xs: 12, md: 16 },
        pb: { xs: 8, md: 12 },
        bgcolor: 'background.default',
        minHeight: '100vh',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          right: 0,
          width: '50%',
          height: '100%',
          background: 'linear-gradient(135deg, rgba(21, 101, 192, 0.03) 0%, rgba(21, 101, 192, 0.08) 100%)',
          borderRadius: '0 0 0 50%',
          display: { xs: 'none', md: 'block' },
        },
      }}
    >
      <Container maxWidth="lg" sx={{ position: 'relative' }}>
        <SectionHeading
          eyebrow={t('contact.eyebrow')}
          title={t('contact.title')}
          subtitle={t('contact.subtitle')}
          sx={{ mb: 8 }}
        />

        <Grid container spacing={6}>
          {/* Contact Form */}
          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 3, md: 4 },
                  borderRadius: 4,
                  bgcolor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider',
                  boxShadow: '0 24px 50px -32px rgba(13,71,161,0.4)',
                }}
              >
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, color: 'text.primary' }}>
                  {t('contact.formTitle')}
                </Typography>
                <Box sx={{ width: 48, height: 4, borderRadius: 2, background: 'linear-gradient(90deg,#1565C0,#FF7A45)', mb: 3 }} />
                <ContactForm />
              </Paper>
            </motion.div>
          </Grid>

          {/* Contact Info — gradient panel */}
          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              style={{ height: '100%' }}
            >
              <Box
                sx={{
                  position: 'relative',
                  overflow: 'hidden',
                  height: '100%',
                  borderRadius: 4,
                  p: { xs: 3, md: 4 },
                  color: 'white',
                  background: gradients.hero,
                  boxShadow: '0 30px 60px -32px rgba(13,71,161,0.6)',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    opacity: 0.5,
                    background:
                      'radial-gradient(circle at 90% 10%, rgba(255,122,69,0.25), transparent 42%), radial-gradient(circle at 10% 90%, rgba(255,255,255,0.1), transparent 40%)',
                  }}
                />

                <Box sx={{ position: 'relative' }}>
                  <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                    {t('contact.info.title')}
                  </Typography>
                  <Box sx={{ width: 48, height: 4, borderRadius: 2, background: 'rgba(255,255,255,0.7)', mb: 3.5 }} />

                  <Stack spacing={2.5}>
                    {contactInfo.map((info, index) => (
                      <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box
                          sx={{
                            width: 46,
                            height: 46,
                            borderRadius: 2.5,
                            bgcolor: 'rgba(255,255,255,0.12)',
                            border: '1px solid rgba(255,255,255,0.18)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}
                        >
                          <info.icon sx={{ color: 'white', fontSize: 22 }} />
                        </Box>
                        {info.href ? (
                          <Link
                            href={info.href}
                            target={info.external ? '_blank' : undefined}
                            rel={info.external ? 'noopener noreferrer' : undefined}
                            onClick={() => trackEvent(info.event, { source: info.eventSource })}
                            sx={{
                              color: 'rgba(255,255,255,0.92)',
                              textDecoration: 'none',
                              fontWeight: 500,
                              '&:hover': { color: '#fff', textDecoration: 'underline' },
                              transition: 'color 0.2s ease',
                            }}
                          >
                            {info.label}
                          </Link>
                        ) : (
                          <Typography sx={{ color: 'rgba(255,255,255,0.92)', fontWeight: 500 }}>
                            {info.label}
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </Stack>
                </Box>

                <Divider sx={{ my: 3.5, borderColor: 'rgba(255,255,255,0.15)', position: 'relative' }} />

                <Box sx={{ position: 'relative', mt: 'auto' }}>
                  <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.8)', display: 'block', mb: 2 }}>
                    {t('contact.quickTitle')}
                  </Typography>
                  <Stack spacing={1.5}>
                    <Button
                      fullWidth
                      href="https://wa.me/905325525984"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => trackEvent('whatsapp_click', { source: 'contact_quick_button' })}
                      startIcon={<WhatsAppIcon />}
                      sx={{
                        justifyContent: 'flex-start',
                        py: 1.25,
                        px: 2.5,
                        fontWeight: 700,
                        color: 'white',
                        background: 'linear-gradient(135deg, #25D366, #128C7E)',
                        boxShadow: '0 10px 22px -10px rgba(18,140,126,0.9)',
                        '&:hover': { background: 'linear-gradient(135deg, #2ee06f, #0f7a6c)' },
                      }}
                    >
                      {t('contact.whatsappBtn')}
                    </Button>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                      <Button
                        fullWidth
                        href="tel:+905325525984"
                        onClick={() => trackEvent('phone_click', { source: 'contact_quick_button' })}
                        startIcon={<PhoneIcon />}
                        sx={{
                          py: 1.25,
                          fontWeight: 700,
                          color: 'white',
                          bgcolor: 'rgba(255,255,255,0.12)',
                          border: '1px solid rgba(255,255,255,0.25)',
                          '&:hover': { bgcolor: 'rgba(255,255,255,0.22)' },
                        }}
                      >
                        {t('contact.callBtn')}
                      </Button>
                      <Button
                        fullWidth
                        href="mailto:miran.iletisimlojistik@hotmail.com"
                        onClick={() => trackEvent('email_click', { source: 'contact_quick_button' })}
                        startIcon={<EmailIcon />}
                        sx={{
                          py: 1.25,
                          fontWeight: 700,
                          color: 'white',
                          bgcolor: 'rgba(255,255,255,0.12)',
                          border: '1px solid rgba(255,255,255,0.25)',
                          '&:hover': { bgcolor: 'rgba(255,255,255,0.22)' },
                        }}
                      >
                        {t('contact.emailBtn')}
                      </Button>
                    </Stack>
                  </Stack>
                </Box>
              </Box>
            </motion.div>
          </Grid>
        </Grid>

        {/* Map */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <Box
            sx={{
              position: 'relative',
              mt: { xs: 6, md: 9 },
              borderRadius: 4,
              overflow: 'hidden',
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: '0 24px 50px -32px rgba(13,71,161,0.4)',
              lineHeight: 0,
            }}
          >
            <LocationMap lat={MAP_LAT} lng={MAP_LNG} />
            <Button
              href={MAP_LINK}
              target="_blank"
              rel="noopener noreferrer"
              variant="contained"
              onClick={() => trackEvent('map_directions_click', { source: 'contact_map_button' })}
              startIcon={<LocationOnIcon />}
              sx={{
                position: 'absolute',
                zIndex: 1000,
                bottom: { xs: 16, md: 20 },
                right: { xs: 16, md: 20 },
                bgcolor: 'background.paper',
                color: 'primary.main',
                fontWeight: 700,
                boxShadow: '0 14px 30px -12px rgba(16,32,50,0.5)',
                '&:hover': { bgcolor: 'background.paper', transform: 'translateY(-2px)' },
                transition: 'all 0.25s ease',
              }}
            >
              {t('contact.directions')}
            </Button>
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};

export default ContactPage;
